'use client';

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { easeBounce } from '@/app/constants/motion';
import { SPARTAN_FEATURE_SHOWCASE_TITLE_HEIGHT_VAR } from '@/app/lcv/components/sections/spartan/products/feature-showcase/constants';
import type { SpartanProductFeatureShowcaseAccordionItemProps } from '@/app/lcv/components/sections/spartan/products/feature-showcase/panel/accordion-item/types';
import { SpartanProductFeatureShowcaseMedia } from '@/app/lcv/components/sections/spartan/products/feature-showcase/panel/media';
import { getSpartanProductFeatureShowcaseDataGA, getSpartanProductFeatureShowcaseTracking, SPARTAN_PRODUCT_FEATURE_SHOWCASE_TRACKING } from '@/app/lcv/components/sections/spartan/products/feature-showcase/tracking';
import { SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
export const SpartanProductFeatureShowcaseAccordionItem = t0 => {
  const $ = _c(48);
  const {
    feature,
    index,
    isOpen,
    isDesktop,
    isLast,
    expandHeight: t1,
    contentRef,
    onToggle,
    onAnimationComplete,
    trackingPrefix: t2
  } = t0;
  const expandHeight = t1 === undefined ? 0 : t1;
  const trackingPrefix = t2 === undefined ? SPARTAN_PRODUCT_FEATURE_SHOWCASE_TRACKING.section : t2;
  const tracking = getSpartanProductFeatureShowcaseTracking(trackingPrefix);
  const dataGA = getSpartanProductFeatureShowcaseDataGA(trackingPrefix);
  const panelId = tracking.featureContent(index);
  const titleId = tracking.featureTitle(index);
  const shouldReduceMotion = useReducedMotion() ?? false;
  const measureRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const duration = shouldReduceMotion ? 0 : isDesktop ? 0.28 : 0.5;
  const openHeight = isDesktop && expandHeight > 0 ? expandHeight : measuredHeight;
  let t3;
  let t4;
  if ($[0] !== contentRef) {
    t3 = () => {
      contentRef?.(measureRef.current);
      return () => {
        contentRef?.(null);
      };
    };
    t4 = [contentRef];
    $[0] = contentRef;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const el = measureRef.current;
      if (!el) {
        return;
      }
      const measure = () => {
        const next = el.scrollHeight ?? 0;
        setMeasuredHeight(prev => prev === next ? prev : next);
      };
      measure();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    };
    t6 = [];
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  if ($[5] !== index || $[6] !== onToggle) {
    t7 = () => {
      onToggle(index);
    };
    $[5] = index;
    $[6] = onToggle;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleToggle = t7;
  let t8;
  if ($[8] !== isDesktop || $[9] !== isOpen || $[10] !== onAnimationComplete) {
    t8 = () => {
      if (isOpen && !isDesktop && measureRef.current?.parentElement) {
        measureRef.current.parentElement.style.height = "auto";
      }
      onAnimationComplete?.();
    };
    $[8] = isDesktop;
    $[9] = isOpen;
    $[10] = onAnimationComplete;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleAnimationComplete = t8;
  const t9 = createDataAttributes({
    test: tracking.feature(index),
    active: isOpen ? true : undefined
  });
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cn("font-noto-bold text-sub-h2 flex w-full shrink-0 cursor-pointer items-center justify-between gap-3 py-3 text-left text-gray-900 md:py-4 xl:box-border xl:py-0", SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME);
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== isDesktop) {
    t11 = isDesktop ? {
      height: `var(${SPARTAN_FEATURE_SHOWCASE_TITLE_HEIGHT_VAR}, auto)`
    } : undefined;
    $[13] = isDesktop;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = createDataAttributes({
    test: tracking.featureToggle(index)
  });
  const t13 = createDataGA({
    click: true,
    eventName: SPARTAN_PRODUCTS_EVENT_NAME,
    group: dataGA.section,
    text: feature.title,
    value: tracking.feature(index)
  });
  let t14;
  if ($[15] !== feature.title) {
    t14 = {
      __html: feature.title
    };
    $[15] = feature.title;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = !isOpen;
  let t16;
  if ($[17] !== t15) {
    t16 = cn("relative size-6 shrink-0", "before:absolute before:inset-0 before:m-auto before:h-0.5 before:w-3 before:rounded-full before:bg-gray-800", "after:ease-bounce after:absolute after:inset-0 after:m-auto after:h-0.5 after:w-3 after:rounded-full after:bg-gray-800 after:transition-transform after:duration-500 after:xl:duration-280", "motion-reduce:after:transition-none", {
      "after:-rotate-90": t15
    });
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t16) {
    t17 = <span className={t16} aria-hidden={true} />;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  const t18 = motion;
  const t19 = "region";
  const t20 = !isOpen ? true : undefined;
  const t21 = false;
  const t22 = isOpen ? openHeight : 0;
  let t23;
  if ($[21] !== t22) {
    t23 = {
      height: t22
    };
    $[21] = t22;
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  let t24;
  if ($[23] !== duration) {
    t24 = {
      duration,
      ease: easeBounce
    };
    $[23] = duration;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  const t25 = isOpen ? openHeight : 0;
  let t26;
  if ($[25] !== t25) {
    t26 = {
      height: t25
    };
    $[25] = t25;
    $[26] = t26;
  } else {
    t26 = $[26];
  }
  const t27 = "overflow-hidden";
  const t28 = createDataAttributes({
    test: tracking.featureContent(index)
  });
  const t29 = "flex flex-col gap-4 pb-5 xl:gap-0 xl:pb-4";
  const t30 = !isDesktop && <SpartanProductFeatureShowcaseMedia src={feature.image.src} alt={feature.title} width={1104} height={621} sizes="(min-width: 768px) calc(100vw - 128px), calc(100vw - 64px)" className="aspect-16-9 w-full" testId={tracking.featureImage(index)} />;
  const t31 = "text-body-1 space-y-1 text-gray-700 [&_p]:m-0";
  let t32;
  if ($[27] !== feature.description) {
    t32 = {
      __html: feature.description
    };
    $[27] = feature.description;
    $[28] = t32;
  } else {
    t32 = $[28];
  }
  const t33 = createDataAttributes({
    test: tracking.featureDescription(index)
  });
  let t34;
  if ($[29] !== t32 || $[30] !== t33) {
    t34 = <div className={t31} dangerouslySetInnerHTML={t32} {...t33} />;
    $[29] = t32;
    $[30] = t33;
    $[31] = t34;
  } else {
    t34 = $[31];
  }
  let t35;
  if ($[32] !== t30 || $[33] !== t34) {
    t35 = <div ref={measureRef} className={t29}>{t30}{t34}</div>;
    $[32] = t30;
    $[33] = t34;
    $[34] = t35;
  } else {
    t35 = $[34];
  }
  let t36;
  if ($[35] !== handleAnimationComplete || $[36] !== panelId || $[37] !== t18.div || $[38] !== t20 || $[39] !== t23 || $[40] !== t24 || $[41] !== t26 || $[42] !== t28 || $[43] !== t35 || $[44] !== titleId) {
    t36 = <t18.div id={panelId} role={t19} aria-labelledby={titleId} inert={t20} initial={t21} animate={t23} transition={t24} onAnimationComplete={handleAnimationComplete} style={t26} className={t27} {...t28}>{t35}</t18.div>;
    $[35] = handleAnimationComplete;
    $[36] = panelId;
    $[37] = t18.div;
    $[38] = t20;
    $[39] = t23;
    $[40] = t24;
    $[41] = t26;
    $[42] = t28;
    $[43] = t35;
    $[44] = titleId;
    $[45] = t36;
  } else {
    t36 = $[45];
  }
  let t37;
  if ($[46] !== isLast) {
    t37 = !isLast && <div className="border-silver-400 shrink-0 border-b" aria-hidden={true} />;
    $[46] = isLast;
    $[47] = t37;
  } else {
    t37 = $[47];
  }
  return <div className="relative shrink-0" {...t9}><button type="button" onClick={handleToggle} className={t10} style={t11} aria-expanded={isOpen} aria-controls={panelId} {...t12} {...t13}><span id={titleId} className="min-w-0" dangerouslySetInnerHTML={t14} {...createDataAttributes({
        test: tracking.featureTitle(index)
      })} />{t17}</button>{t36}{t37}</div>;
};